import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import i18n from 'i18next'

import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import minMax from 'dayjs/plugin/minMax'
import MultiSelect from '../../components/MultiSelect'
import { ForkliftContractManagementWrapper } from './styles'
import { SearchButton } from '../../components/Button'
import ContractDetail from '../../components/ContractDetail'
import YearClick from '../../components/YearClick'
import { CircularProgress } from '@material-ui/core'
import Modal from '../../components/Modal/index'
import { getOrganizationList } from '../../features/organizations/organizationSlice'
import { getForkliftContract, resetState, getForkliftContractDetail } from '../../features/forkliftContracts/forkliftContractSlice'
import { useLocation } from 'react-router-dom'
import { forkliftContractColumnMap } from '../../components/Table/columnNameMap'
dayjs.extend(quarterOfYear)
dayjs.extend(minMax)

function ForkliftContractManagement () {
  const dispatch = useDispatch()
  const location = useLocation()
  const [selectedView] = React.useState('monthly')
  const [currentYear, setYear] = React.useState(dayjs().format('YYYY'))
  const [company, setCompany] = React.useState('')
  const [focusElement, setFocus] = React.useState(dayjs())
  const [focusQuarter] = React.useState(dayjs().format('YYYY-MM-DD'))
  const [searchDate, setSearchDate] = React.useState(dayjs().startOf('month').format('YYYY-MM-DD'))
  const [branchs, setBranchs] = React.useState(location.state?.organizationIdDefault ? location.state.organizationIdDefault : [])
  const [lastFetch, setLastFetch] = React.useState('')
  const [fromDate, setFromDate] = React.useState(dayjs().startOf('month').format('YYYY-MM-DD'))
  const [toDate, setToDate] = React.useState(dayjs().endOf('month').format('YYYY-MM-DD'))
  const [highlightedRow, setHighlightedRow] = React.useState(null)
  const contractState = useSelector(state => state.forkliftContract)
  const organizationState = useSelector(state => state.organization)

  React.useEffect(() => {
    dispatch(getOrganizationList())
    dispatch(resetState())
    const today = new Date()
    const month = today.getMonth()
    setHighlightedRow(month)

    if (!location.state) return
    const { companyName, organizationIdDefault } = location.state
    searchDataInState(location.state.period, organizationIdDefault, companyName)
  }, [])

  const getCompanyOptions = () => {
    let companyOptions = [...new Set(organizationState.list.map(item => item.company))]
    companyOptions = companyOptions.map(item => { return { key: item, value: item } })
    return [{ key: '', value: i18n.t('COMMON_COMMON_DROPDOWN-OPTION-ALL_LABEL') }, ...companyOptions]
  }

  const handleCompanyChange = (e) => {
    setCompany(e[0].key)
  }

  const handleBranchChange = (e) => {
    setBranchs(e.map(item => item.id))
  }

  const searchDataInState = (period, organizationId, companyName) => {
    const { from, to, year, focus } = period
    setBranchs(organizationId)
    setYear(year)
    let dayjsFrom = dayjs(from)
    let dayjsTo = dayjs(to)
    const stateJSON = JSON.stringify(location.state)
    const linkState = JSON.parse(stateJSON)
    if (location.state.type === 'annualInspection') {
      const annualInspectionDate = linkState.period.from
      dayjsFrom = dayjs(annualInspectionDate).add(1, 'year').startOf('month')
      dayjsTo = dayjs(annualInspectionDate).add(1, 'year').endOf('month')
    }
    setHighlightedRow(dayjsFrom.month())
    setFocus(focus)
    setFromDate(dayjsFrom.format('YYYY-MM-DD'))
    setToDate(dayjsTo.format('YYYY-MM-DD'))
    setSearchDate(dayjsFrom.format('YYYY-MM-DD'))
    const selectedId = branchs.length > 0 ? branchs : []
    const body = {
      year,
      organizationId: selectedId
    }

    setLastFetch(selectedId)
    dispatch(getForkliftContract({ body }))
    dispatch(getForkliftContractDetail({
      body: {
        from: dayjsFrom.format('YYYY-MM-DD'),
        to: dayjsTo.format('YYYY-MM-DD'),
        organizationId: selectedId
      }
    }))
  }

  const onClickSearch = () => {
    location.state = undefined
    const selectedId = branchs.length > 0 ? branchs : ''

    const body = {
      year: currentYear,
      organizationId: selectedId || []
    }
    dispatch(getForkliftContract({ body }))
    dispatch(getForkliftContractDetail({
      body: {
        from: fromDate,
        to: toDate,
        organizationId: selectedId || []
      }
    }
    ))

    setLastFetch(selectedId || [])
    setSearchDate(fromDate)
  }

  const middleHop = year => {
    setYear(year)
    setDateChange(year)
  }

  const setMiddleFocus = e => {
    setHighlightedRow(e)
    setFocus(e)
    const month = contractState.data[e].period
    const selectMonth = dayjs().year(currentYear).month(month - 1).date(1)
    setFromDate(selectMonth.startOf('month').format('YYYY-MM-DD'))
    setToDate(selectMonth.endOf('month').format('YYYY-MM-DD'))
    setSearchDate(selectMonth.startOf('month').format('YYYY-MM-DD'))
    if (lastFetch) {
      dispatch(getForkliftContractDetail({
        body: {
          from: selectMonth.startOf('month').format('YYYY-MM-DD'),
          to: selectMonth.endOf('month').format('YYYY-MM-DD'),
          organizationId: lastFetch
        }
      }))
    }
  }

  const setDateChange = e => {
    setFromDate(dayjs(e + fromDate.replaceAll('-', '').substring(4, 8)).format('YYYY-MM-DD') || '')
    setToDate(dayjs(e + toDate.replaceAll('-', '').substring(4, 8)).format('YYYY-MM-DD') || '')
  }

  const type = [
    { title: 'CONTRACT_CONTRACT-MANAGEMENT_INSPECTION_LABEL', key: 'inspection', id: 'inspection' },
    { title: 'CONTRACT_CONTRACT-MANAGEMENT_LEASE_LABEL', key: 'lease', id: 'lease' },
    { title: 'CONTRACT-FORKLIFT_COMMON_ANNUALINSPECTIONDATE_LABEL', key: 'annualInspection', id: 'annual-inspection' },
    { title: 'CONTRACT_CONTRACT-MANAGEMENT_MANDATORY-INSURANCE_LABEL', key: 'mandatoryInsurance', id: 'mandatory-insurance' },
    { title: 'CONTRACT-FORKLIFT_COMMON_RENTALENDDATE_LABEL', key: 'rental', id: 'rental' },
    { title: 'CONTRACT_CONTRACT-MANAGEMENT_VOLUNTARY-INSURANCE_LABEL', key: 'voluntaryInsurance', id: 'voluntary-insurance' }
  ]

  const propTypes = '../forklift'

  const rowClickEvent = (index) => {
    setMiddleFocus(index)
  }

  const renderColumnName = d => {
    const result = []
    for (const i in d) {
      result.push(<td key={i} className={`column-name column-${d[i]}`}>{i18n.t(d[i])}</td>)
    }
    return <thead className="header"><tr>{result}</tr></thead>
  }

  const renderData = list => {
    // no data
    if (list && list.length === 0) {
      return (
        <tbody className="table">
          <tr>
            <td className="table-message">{i18n.t('COMMON_COMMON_DATA-NOT-FOUND_LABEL')}</td>
          </tr>
        </tbody>
      )
    }
    // initial
    if (!list) {
      list = []
    }
    let result = []

    result = list.map((item, index) => {
      const i = { ...item }
      i.editable = false
      const row = []
      let dex = 0
      for (const j in forkliftContractColumnMap) {
        let value = i[j]
        if (i.data && j !== 'period') {
          value = i.data[j]
        } else if (j === 'period') {
          value = i.period + '月'
        }
        row.push(<td data-testid='render-value' key={dex} className="value">{value}</td>)
        dex = ++dex
      }

      return <tr onClick={() => rowClickEvent(index)} id={list[index].id} key={index} className={index === highlightedRow ? 'row selected-row' : 'row'}>
        {row}
      </tr>
    })
    return <tbody className="table">{result}</tbody>
  }

  return (
    <ForkliftContractManagementWrapper>
      <section className="header">
        <div className="header-title">{i18n.t('CONTRACT_FORKLIFT-CONTRACT-MANAGEMENT_PAGE-HEADER_LABEL')}</div>
      </section>
      <section className="header">
        <div className="header-actions">
          <div className="action-options">
            <label>{i18n.t('COMMON_COMMON_TARGET-YEAR_LABEL')}</label>
            <YearClick onChange={middleHop} year={currentYear} />
          </div>
          <div className="action-options">
            <label>{i18n.t('COMMON_COMMON_COMPANY-DROPDOWN_LABEL')}</label>
            <MultiSelect
              width="100%"
              placeholder={i18n.t('COMMON_COMMON_DROPDOWN-PLEASE-SELECT_LABEL')}
              options={getCompanyOptions()}
              valueField="key"
              labelField="value"
              onChange={handleCompanyChange}
              defaultValue={(location.state && location.state.organizationIdDefault.length > 0) ? location.state.companyName : company }
            />
          </div>
          <div className="action-options">
            <label>{i18n.t('COMMON_COMMON_BRANCH-DROPDOWN_LABEL')}</label>
            <MultiSelect
              disabled={!company}
              width="100%"
              placeholder={i18n.t('COMMON_COMMON_DROPDOWN-PLEASE-SELECT_LABEL')}
              multi={true}
              allItem={true}
              options={organizationState.list.filter(item => item.company === company)}
              valueField="id"
              labelField="branch"
              onChange={handleBranchChange}
              defaultValue={(location.state && location.state.organizationIdDefault.length > 0) && location.state.organizationIdDefault}
            />
          </div>
          <div className="action-options"></div>
          <div className="action-options"></div>
          <div className="action-options" style={{ alignItems: 'flex-end', marginRight: '24px' }}>
            <div className="submit-container">
              <SearchButton data-testid="search-button" onClick={onClickSearch} />
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div>
          <h3>{i18n.t('CONTRACT_FORKLIFT-CONTRACT-MANAGEMENT_SECTION-HEADER_LABEL')}</h3>
          <div className="display-month-type-wrapper">
          <table>
            {renderColumnName(forkliftContractColumnMap)}
            {renderData(contractState.data)}
          </table>
          </div>
        </div>
        <div>
          <h3>{i18n.t('CONTRACT_FORKLIFT-CONTRACT-MANAGEMENT_SUBSECTION-HEADER_LABEL')}</h3>
          <div className="input-date-wrapper">
            <div className="label-container">
              <span>{i18n.t('FORKLIFT_FORKLIFT-MANAGEMENT_DATE-FILTER_LABEL')}</span>
              <span id="selectYaerMonth" className='select-yaer-month'>{dayjs(searchDate).format('YYYY/MM')}</span>
            </div>
          </div>
          <ContractDetail period={{ from: fromDate, to: toDate, view: selectedView, year: currentYear, focus: focusElement, focusQuarter }} org={!location.state ? organizationState.list : location.state.organizationList} data={contractState.detail} lastFetchIds={lastFetch} type={type} propTypes={propTypes}/>
        </div>
      </section>
      <Modal show={contractState.load}><CircularProgress></CircularProgress></Modal>
    </ForkliftContractManagementWrapper>
  )
}

export default ForkliftContractManagement

ForkliftContractManagement.propTypes = {
  history: PropTypes.object
}
